/**
 * PDL's schemas, read as data rather than restated as code.
 *
 * The column picker, the filter typeahead and Webster's vocabulary all read a catalogue file, so
 * replacing `fieldCatalogue.json` replaces all three for people and `companyCatalogue.json` does the
 * same for companies. This module is the type they are read through and nothing more.
 *
 * ⚑ A catalogue is a *value*, not a module singleton (ORB-762). Until the second object arrived the
 * person catalogue was exported as a bare `FIELDS` array and a `fieldByKey(key)` that closed over
 * it, and nine modules read it that way. Two objects make that shape actively wrong rather than
 * merely narrow: a company-only key handed to a person-only `fieldByKey` resolves to `undefined`,
 * and every consumer treats `undefined` as "the catalogue does not publish this field". A chip would
 * render a raw key, and `filterUrl` — which rejects an unknown key on purpose, so a hand-written URL
 * cannot filter on an excluded field — would drop those clauses from a shared company link and land
 * on the unfiltered list. Threading the catalogue is what makes a third object cost nothing.
 *
 * Note what this is *not*: the two catalogues share seventeen keys, deliberately, so resolving a
 * clause against the wrong catalogue is not reliably a failure. That is why every clause names its
 * object (ORB-815) and every lookup below takes one.
 *
 * `sex`, `birth_date`, `birth_year` and `street_addresses` are absent from the person catalogue on
 * purpose — B2C fields that carry compliance weight and buy a demand-gen platform nothing. Absence
 * is the enforcement: no picker, no filter and no agent can offer a field that is not in the file.
 */

import personCatalogue from './fieldCatalogue.json'
import companyCatalogue from './companyCatalogue.json'
import filterOperators from './filterOperators.json'
import employerFields from './employerFields.json'
import type { FilterOperator } from './adapter'

/** The objects the console renders. A third one is a catalogue file and an entry in `CATALOGUES`. */
export type ConsoleObject = 'person' | 'company'

export type FieldType = 'string' | 'number' | 'date' | 'enum' | 'bool' | 'string[]'

/**
 * Four provenances, not two. The dot in a column header reads: solid magenta for vendor stated,
 * hollow for vendor inferred, a sparkle for anything a model of ours wrote, nothing for first party.
 */
export type Provenance = 'first_party' | 'vendor_stated' | 'vendor_inferred' | 'ours_ai'

/**
 * `pdl_published` is a number PDL publishes and the mock must honour. `estimated` is a placeholder
 * until the sample file lands — shaped to be plausible, never to be cited.
 */
export type FillRateSource = 'pdl_published' | 'estimated'

export interface FieldDef {
  key: string
  label: string
  group: string
  type: FieldType
  provenance: Provenance
  /** 0..1. The header renders it as a percentage of the current result set. */
  fillRate: number
  fillRateSource: FillRateSource
  cardinality?: number
  values?: string[]
  /** Computed at ingest from other fields. Orthogonal to provenance. */
  derived?: boolean
  derivedFrom?: string[]
  /** Costs a credit to see. */
  revealable?: boolean
  width: number
  /** Minimum usable width for a cell containing an action. */
  minWidth?: number
  align?: 'right'
  defaultVisible?: boolean
  pinned?: boolean
  flex?: boolean
  sortable: boolean
  defaultSort?: 'asc' | 'desc'
  note?: string
}

export interface CatalogueMeta {
  vendor: string
  release: string
  object: ConsoleObject
  fieldCount: number
  schemaFieldCount: number
  subset: boolean
  publishedFields: string[]
  excludedFields: { reason: string; fields: string[] }
  /**
   * Fields the store holds but a list query must never carry. Present on the company catalogue,
   * where the monthly time series alone is about 55% of a 139KB record — see `CompanySidecar`.
   */
  sidecarFields?: { reason: string; fields: string[] }
  /**
   * Schema fields kept out of the catalogue altogether — unbounded maps and arrays that are not a
   * cell at any density and that no filter clause can address. Recorded rather than silently
   * dropped, so `fieldCount + aggregate + sidecar + excluded` reconciles to `schemaFieldCount` and
   * nobody has to re-derive that arithmetic when the sample file lands.
   */
  aggregateFields?: { reason: string; fields: string[] }
}

interface CatalogueFile {
  _meta: CatalogueMeta
  fields: FieldDef[]
}

/**
 * One object's fields, and everything derived from them, computed once.
 *
 * The derived members are the ones a grid needs on every render — the id sets a table is built from
 * and the layout is persisted against. Deriving them per render would walk 73 fields on every scroll
 * frame; deriving them per object at module load walks them once.
 */
export interface FieldCatalogue {
  object: ConsoleObject
  meta: CatalogueMeta
  fields: FieldDef[]
  byKey(key: string): FieldDef | undefined
  /** Catalogue order for every field. The fallback when nothing has been reordered. */
  allIds: string[]
  /** The ids the catalogue opens on, in catalogue order. */
  defaultVisibleIds: string[]
  /** Identity columns. Sticky, never hideable, and what a sticky offset is computed from. */
  pinnedIds: string[]
  /** The catalogue's opening sort, if it declares one. */
  defaultSort: { field: string; direction: 'asc' | 'desc' } | null
}

/**
 * A pinned column is positioned by summing the widths of the pinned columns before it, so it has to
 * have one. A flex column has no fixed width by definition, which makes it unpinnable.
 *
 * Thrown at module load rather than defaulted: a pinned column that silently falls back to some
 * width renders half a column-width out of position at every scroll offset, and that reads as a
 * rendering glitch rather than as the catalogue error it actually is. It lives here rather than in
 * the table builder so that a catalogue is checked once, whoever reads it.
 */
function assertUsable(file: CatalogueFile, object: ConsoleObject): void {
  if (file._meta.object !== object) {
    throw new Error(`catalogue: file declares object "${file._meta.object}" but was loaded as "${object}".`)
  }
  for (const field of file.fields) {
    if (!field.pinned) continue
    if (field.flex) {
      throw new Error(`${object} catalogue: "${field.key}" is pinned and flex; a pinned column needs a fixed width.`)
    }
    if (!Number.isFinite(field.width) || field.width <= 0) {
      throw new Error(`${object} catalogue: pinned column "${field.key}" has no usable width (${field.width}).`)
    }
  }
}

function load(raw: unknown, object: ConsoleObject): FieldCatalogue {
  const file = raw as CatalogueFile
  assertUsable(file, object)

  const byKey = new Map(file.fields.map(field => [field.key, field]))
  const sorted = file.fields.find(field => field.defaultSort)

  return {
    object,
    meta: file._meta,
    fields: file.fields,
    byKey: key => byKey.get(key),
    allIds: file.fields.map(field => field.key),
    defaultVisibleIds: file.fields.filter(field => field.defaultVisible).map(field => field.key),
    pinnedIds: file.fields.filter(field => field.pinned).map(field => field.key),
    defaultSort: sorted ? { field: sorted.key, direction: sorted.defaultSort! } : null,
  }
}

export const PERSON_CATALOGUE: FieldCatalogue = load(personCatalogue, 'person')
export const COMPANY_CATALOGUE: FieldCatalogue = load(companyCatalogue, 'company')

const CATALOGUES: Record<ConsoleObject, FieldCatalogue> = {
  person: PERSON_CATALOGUE,
  company: COMPANY_CATALOGUE,
}

export function catalogueFor(object: ConsoleObject): FieldCatalogue {
  return CATALOGUES[object]
}

export function fieldFor(object: ConsoleObject, key: string): FieldDef | undefined {
  return CATALOGUES[object].byKey(key)
}

/**
 * A field's label as a chip reads it. A company field says so — "Company size band" — because a
 * person's Country is not their employer's. Left alone when the label already starts with "Company",
 * and when its first word is an acronym ("PDL profiles", "HQ").
 */
export function fieldLabel(object: ConsoleObject, key: string): string {
  const label = fieldFor(object, key)?.label ?? key
  if (object === 'person' || /^company\b/i.test(label)) return label
  return `Company ${/^[A-Z][a-z]*\b/.test(label) ? label[0].toLowerCase() + label.slice(1) : label}`
}

/**
 * Company fields every person row already carries for that person's employer, as company key →
 * person key (ORB-815). The API's compiler reads a byte-identical copy.
 */
export const EMPLOYER_FIELDS: Readonly<Record<string, string>> = employerFields.companyToPerson

/**
 * Which operators each type accepts — read as data, for the reason the catalogues are (ORB-793).
 *
 * ⚑⚑ This was a `switch` here and a second `switch` in `ConsoleFilterCompiler.cs`, and nothing
 * failed when the two disagreed. They did: the server compiled `equals` on an `enum` and ran it,
 * this side refused the same clause on arrival, and a filter that crossed between them through a
 * URL was discarded in full and in silence. One file, copied into both trees and compared
 * byte-for-byte by `ConsoleFieldCatalogueTests`, is what makes that drift a failing build.
 *
 * The reasoning behind the narrower rows — three operators on a date, one on a bool — travels in
 * the file's own `_notes`, so it stays beside the rule it explains rather than beside one copy.
 */
const OPERATORS_BY_TYPE = filterOperators.byType as Record<FieldType, FilterOperator[]>

/** Operators the filter bar may offer for a field, keyed off its type. */
export function operatorsFor(type: FieldType): FilterOperator[] {
  return OPERATORS_BY_TYPE[type]
}
